import { FragmentType, getFragmentData } from '@/__generated__';
import { CancellationSource } from '@/__generated__/graphql';
import { useMutation } from '@apollo/client';
import { Badge, Button, Group, Stack, Text } from '@mantine/core';
import { IconAlertTriangle } from '@tabler/icons-react';
import dayjs from 'dayjs';
import { DataTable } from 'mantine-datatable';
import { orderCancellationDetailsFragment } from '../graphql/fragments';
import { sendMessageDocument } from '../graphql/mutations';

const sourceColors = {
  [CancellationSource.Admin]: 'blue',
  [CancellationSource.Customer]: 'grape',
};

const sourceTranslations = {
  [CancellationSource.Admin]: 'Admin',
  [CancellationSource.Customer]: 'Zákazník',
};

const warningMessage =
  'Upozornenie: pri ďalšom zrušení produktu Vám budeme musieť na určitý čas zablokovať objednávanie.';

export const OrderCancellationsTable = ({
  orderId,
  ...props
}: {
  orderId: string;
  cancellations?: FragmentType<typeof orderCancellationDetailsFragment>[] | null;
}) => {
  const cancellations = getFragmentData(orderCancellationDetailsFragment, props.cancellations);

  const [sendMessage, sendMessageMutation] = useMutation(sendMessageDocument, {
    refetchQueries: 'active',
    awaitRefetchQueries: true,
  });

  const exchangedCount = cancellations?.filter(({ reason }) => reason === 'Výmena').length ?? 0;
  const problemCount = cancellations?.filter(({ reason }) => reason === 'Problém').length ?? 0;
  const cancelledCount = (cancellations?.length ?? 0) - exchangedCount - problemCount;

  return (
    <Stack gap="xs">
      <Group justify="space-between">
        <Group gap="xs">
          <Text fw={500}>Zrušené produkty ({cancellations?.length ?? 0})</Text>
          <Badge color="red" variant="light">
            Zrušené: {cancelledCount}
          </Badge>
          <Badge color="orange" variant="light">
            Vymenené: {exchangedCount}
          </Badge>
          <Badge color="gray" variant="light">
            Problém: {problemCount}
          </Badge>
        </Group>
        <Button
          size="compact-sm"
          color="red"
          variant="light"
          leftSection={<IconAlertTriangle size={14} />}
          loading={sendMessageMutation.loading}
          onClick={() => {
            void sendMessage({ variables: { orderId, message: warningMessage } });
          }}
        >
          Upozorniť na blokovanie
        </Button>
      </Group>
      <DataTable
        withTableBorder
        borderRadius="sm"
        records={cancellations ?? []}
        height="auto"
        rowBackgroundColor={({ reason }) => {
          if (reason === 'Problém') return undefined;

          return reason === 'Výmena'
            ? { light: '#fff4e6', dark: '#3a2a1a' }
            : { light: '#fff5f5', dark: '#3a1d1d' };
        }}
        columns={[
          { accessor: 'lineCode', title: 'Kód' },
          { accessor: 'lineName', title: 'Názov' },
          {
            accessor: 'linePrice',
            title: 'Cena',
            render: ({ linePrice }) => `${linePrice} €`,
          },
          {
            accessor: 'source',
            title: 'Zdroj',
            render: ({ source }) => (
              <Badge color={sourceColors[source]}>{sourceTranslations[source]}</Badge>
            ),
          },
          {
            accessor: 'reason',
            title: 'Dôvod',
            render: ({ reason }) => reason ?? '—',
          },
          {
            accessor: 'cancelledAt',
            title: 'Zrušené',
            render: ({ cancelledAt }) => dayjs(cancelledAt).format('DD MMM YYYY HH:mm'),
          },
        ]}
      />
    </Stack>
  );
};
